/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AgentConnectionDto,
  AgentConnectionRequestDto,
  ApiErrorDto,
  ApiKeyDto,
  ApproveAgentConnectionDto,
  CreateApiKeyDto,
  CreatedApiKeyDto,
  UpdateAgentConnectionDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Agents approved in the workspace, newest first. Owners see everyone's; others their own.
 */
export const listAgentConnections = (id: string, signal?: AbortSignal) => {
  return customInstance<AgentConnectionDto[]>({
    url: `/api/v1/workspaces/${id}/agent-connections`,
    method: 'GET',
    signal,
  });
};

export const getListAgentConnectionsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/agent-connections`] as const;
};

export const getListAgentConnectionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAgentConnections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAgentConnections>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAgentConnectionsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAgentConnections>>> = ({
    signal,
  }) => listAgentConnections(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listAgentConnections>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAgentConnectionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAgentConnections>>
>;
export type ListAgentConnectionsQueryError = ErrorType<ApiErrorDto>;

export function useListAgentConnections<
  TData = Awaited<ReturnType<typeof listAgentConnections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAgentConnections>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAgentConnections>>,
          TError,
          Awaited<ReturnType<typeof listAgentConnections>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAgentConnections<
  TData = Awaited<ReturnType<typeof listAgentConnections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAgentConnections>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAgentConnections>>,
          TError,
          Awaited<ReturnType<typeof listAgentConnections>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAgentConnections<
  TData = Awaited<ReturnType<typeof listAgentConnections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAgentConnections>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Agents approved in the workspace, newest first. Owners see everyone's; others their own.
 */

export function useListAgentConnections<
  TData = Awaited<ReturnType<typeof listAgentConnections>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listAgentConnections>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAgentConnectionsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Change an agent's role. Whoever approved it, or an owner.
 */
export const updateAgentConnection = (
  id: string,
  connectionId: string,
  updateAgentConnectionDto: BodyType<UpdateAgentConnectionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/agent-connections/${connectionId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateAgentConnectionDto,
    signal,
  });
};

export const getUpdateAgentConnectionMutationKey = () =>
  ['updateAgentConnection'] as const;

export const getUpdateAgentConnectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAgentConnection>>,
    TError,
    UpdateAgentConnectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAgentConnection>>,
  TError,
  UpdateAgentConnectionMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateAgentConnectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAgentConnection>>,
    UpdateAgentConnectionMutationVariables
  > = (props) => {
    const { id, connectionId, data } = props ?? {};

    return updateAgentConnection(id, connectionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAgentConnectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateAgentConnection>>
>;
export type UpdateAgentConnectionMutationBody = BodyType<UpdateAgentConnectionDto>;
export type UpdateAgentConnectionMutationError = ErrorType<ApiErrorDto>;
export type UpdateAgentConnectionMutationVariables = {
  id: string;
  connectionId: string;
  data: BodyType<UpdateAgentConnectionDto>;
};

/**
 * @summary Change an agent's role. Whoever approved it, or an owner.
 */
export const useUpdateAgentConnection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateAgentConnection>>,
      TError,
      UpdateAgentConnectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateAgentConnection>>,
  TError,
  UpdateAgentConnectionMutationVariables,
  TContext
> => {
  return useMutation(getUpdateAgentConnectionMutationOptions(options), queryClient);
};
/**
 * @summary Revoke an agent; its next request is refused. Whoever approved it, or an owner.
 */
export const revokeAgentConnection = (
  id: string,
  connectionId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/agent-connections/${connectionId}`,
    method: 'DELETE',
    signal,
  });
};

export const getRevokeAgentConnectionMutationKey = () =>
  ['revokeAgentConnection'] as const;

export const getRevokeAgentConnectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeAgentConnection>>,
    TError,
    RevokeAgentConnectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeAgentConnection>>,
  TError,
  RevokeAgentConnectionMutationVariables,
  TContext
> => {
  const mutationKey = getRevokeAgentConnectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeAgentConnection>>,
    RevokeAgentConnectionMutationVariables
  > = (props) => {
    const { id, connectionId } = props ?? {};

    return revokeAgentConnection(id, connectionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeAgentConnectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeAgentConnection>>
>;

export type RevokeAgentConnectionMutationError = ErrorType<ApiErrorDto>;
export type RevokeAgentConnectionMutationVariables = { id: string; connectionId: string };

/**
 * @summary Revoke an agent; its next request is refused. Whoever approved it, or an owner.
 */
export const useRevokeAgentConnection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeAgentConnection>>,
      TError,
      RevokeAgentConnectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeAgentConnection>>,
  TError,
  RevokeAgentConnectionMutationVariables,
  TContext
> => {
  return useMutation(getRevokeAgentConnectionMutationOptions(options), queryClient);
};
/**
 * @summary API keys that still work, newest first. Owners see everyone's; others their own.
 */
export const listApiKeys = (id: string, signal?: AbortSignal) => {
  return customInstance<ApiKeyDto[]>({
    url: `/api/v1/workspaces/${id}/api-keys`,
    method: 'GET',
    signal,
  });
};

export const getListApiKeysQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/api-keys`] as const;
};

export const getListApiKeysQueryOptions = <
  TData = Awaited<ReturnType<typeof listApiKeys>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListApiKeysQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listApiKeys>>> = ({ signal }) =>
    listApiKeys(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListApiKeysQueryResult = NonNullable<Awaited<ReturnType<typeof listApiKeys>>>;
export type ListApiKeysQueryError = ErrorType<ApiErrorDto>;

export function useListApiKeys<
  TData = Awaited<ReturnType<typeof listApiKeys>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listApiKeys>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListApiKeys<
  TData = Awaited<ReturnType<typeof listApiKeys>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listApiKeys>>,
          TError,
          Awaited<ReturnType<typeof listApiKeys>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListApiKeys<
  TData = Awaited<ReturnType<typeof listApiKeys>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary API keys that still work, newest first. Owners see everyone's; others their own.
 */

export function useListApiKeys<
  TData = Awaited<ReturnType<typeof listApiKeys>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listApiKeys>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListApiKeysQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create an API key. The response is the only time the key is shown.
 */
export const createApiKey = (
  id: string,
  createApiKeyDto: BodyType<CreateApiKeyDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CreatedApiKeyDto>({
    url: `/api/v1/workspaces/${id}/api-keys`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createApiKeyDto,
    signal,
  });
};

export const getCreateApiKeyMutationKey = () => ['createApiKey'] as const;

export const getCreateApiKeyMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createApiKey>>,
    TError,
    CreateApiKeyMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createApiKey>>,
  TError,
  CreateApiKeyMutationVariables,
  TContext
> => {
  const mutationKey = getCreateApiKeyMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createApiKey>>,
    CreateApiKeyMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createApiKey(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof createApiKey>>
>;
export type CreateApiKeyMutationBody = BodyType<CreateApiKeyDto>;
export type CreateApiKeyMutationError = ErrorType<ApiErrorDto>;
export type CreateApiKeyMutationVariables = {
  id: string;
  data: BodyType<CreateApiKeyDto>;
};

/**
 * @summary Create an API key. The response is the only time the key is shown.
 */
export const useCreateApiKey = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createApiKey>>,
      TError,
      CreateApiKeyMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createApiKey>>,
  TError,
  CreateApiKeyMutationVariables,
  TContext
> => {
  return useMutation(getCreateApiKeyMutationOptions(options), queryClient);
};
/**
 * @summary Revoke an API key; requests with it are refused from now on. Its creator, or an owner.
 */
export const revokeApiKey = (id: string, keyId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/api-keys/${keyId}`,
    method: 'DELETE',
    signal,
  });
};

export const getRevokeApiKeyMutationKey = () => ['revokeApiKey'] as const;

export const getRevokeApiKeyMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeApiKey>>,
    TError,
    RevokeApiKeyMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeApiKey>>,
  TError,
  RevokeApiKeyMutationVariables,
  TContext
> => {
  const mutationKey = getRevokeApiKeyMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeApiKey>>,
    RevokeApiKeyMutationVariables
  > = (props) => {
    const { id, keyId } = props ?? {};

    return revokeApiKey(id, keyId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeApiKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeApiKey>>
>;

export type RevokeApiKeyMutationError = ErrorType<ApiErrorDto>;
export type RevokeApiKeyMutationVariables = { id: string; keyId: string };

/**
 * @summary Revoke an API key; requests with it are refused from now on. Its creator, or an owner.
 */
export const useRevokeApiKey = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeApiKey>>,
      TError,
      RevokeApiKeyMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeApiKey>>,
  TError,
  RevokeApiKeyMutationVariables,
  TContext
> => {
  return useMutation(getRevokeApiKeyMutationOptions(options), queryClient);
};
/**
 * @summary The signed-in user's agent connection, and the workspaces they could approve it for.
 */
export const getAgentConnectionRequest = (connectionId: string, signal?: AbortSignal) => {
  return customInstance<AgentConnectionRequestDto>({
    url: `/api/v1/agent-connections/${connectionId}`,
    method: 'GET',
    signal,
  });
};

export const getGetAgentConnectionRequestQueryKey = (connectionId: string) => {
  return [`/api/v1/agent-connections/${connectionId}`] as const;
};

export const getGetAgentConnectionRequestQueryOptions = <
  TData = Awaited<ReturnType<typeof getAgentConnectionRequest>>,
  TError = ErrorType<ApiErrorDto>,
>(
  connectionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAgentConnectionRequest>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetAgentConnectionRequestQueryKey(connectionId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAgentConnectionRequest>>> = ({
    signal,
  }) => getAgentConnectionRequest(connectionId, signal);

  return {
    queryKey,
    queryFn,
    enabled: connectionId !== null && connectionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getAgentConnectionRequest>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAgentConnectionRequestQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAgentConnectionRequest>>
>;
export type GetAgentConnectionRequestQueryError = ErrorType<ApiErrorDto>;

export function useGetAgentConnectionRequest<
  TData = Awaited<ReturnType<typeof getAgentConnectionRequest>>,
  TError = ErrorType<ApiErrorDto>,
>(
  connectionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAgentConnectionRequest>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAgentConnectionRequest>>,
          TError,
          Awaited<ReturnType<typeof getAgentConnectionRequest>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAgentConnectionRequest<
  TData = Awaited<ReturnType<typeof getAgentConnectionRequest>>,
  TError = ErrorType<ApiErrorDto>,
>(
  connectionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAgentConnectionRequest>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAgentConnectionRequest>>,
          TError,
          Awaited<ReturnType<typeof getAgentConnectionRequest>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAgentConnectionRequest<
  TData = Awaited<ReturnType<typeof getAgentConnectionRequest>>,
  TError = ErrorType<ApiErrorDto>,
>(
  connectionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAgentConnectionRequest>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The signed-in user's agent connection, and the workspaces they could approve it for.
 */

export function useGetAgentConnectionRequest<
  TData = Awaited<ReturnType<typeof getAgentConnectionRequest>>,
  TError = ErrorType<ApiErrorDto>,
>(
  connectionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAgentConnectionRequest>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAgentConnectionRequestQueryOptions(connectionId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Let the agent work in a workspace, with a role.
 */
export const approveAgentConnection = (
  connectionId: string,
  approveAgentConnectionDto: BodyType<ApproveAgentConnectionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<AgentConnectionRequestDto>({
    url: `/api/v1/agent-connections/${connectionId}/approve`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: approveAgentConnectionDto,
    signal,
  });
};

export const getApproveAgentConnectionMutationKey = () =>
  ['approveAgentConnection'] as const;

export const getApproveAgentConnectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof approveAgentConnection>>,
    TError,
    ApproveAgentConnectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof approveAgentConnection>>,
  TError,
  ApproveAgentConnectionMutationVariables,
  TContext
> => {
  const mutationKey = getApproveAgentConnectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof approveAgentConnection>>,
    ApproveAgentConnectionMutationVariables
  > = (props) => {
    const { connectionId, data } = props ?? {};

    return approveAgentConnection(connectionId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ApproveAgentConnectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof approveAgentConnection>>
>;
export type ApproveAgentConnectionMutationBody = BodyType<ApproveAgentConnectionDto>;
export type ApproveAgentConnectionMutationError = ErrorType<ApiErrorDto>;
export type ApproveAgentConnectionMutationVariables = {
  connectionId: string;
  data: BodyType<ApproveAgentConnectionDto>;
};

/**
 * @summary Let the agent work in a workspace, with a role.
 */
export const useApproveAgentConnection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof approveAgentConnection>>,
      TError,
      ApproveAgentConnectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof approveAgentConnection>>,
  TError,
  ApproveAgentConnectionMutationVariables,
  TContext
> => {
  return useMutation(getApproveAgentConnectionMutationOptions(options), queryClient);
};
/**
 * @summary Turn the agent away; it can sign in again later to ask again.
 */
export const declineAgentConnection = (connectionId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/agent-connections/${connectionId}/decline`,
    method: 'POST',
    signal,
  });
};

export const getDeclineAgentConnectionMutationKey = () =>
  ['declineAgentConnection'] as const;

export const getDeclineAgentConnectionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof declineAgentConnection>>,
    TError,
    DeclineAgentConnectionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof declineAgentConnection>>,
  TError,
  DeclineAgentConnectionMutationVariables,
  TContext
> => {
  const mutationKey = getDeclineAgentConnectionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof declineAgentConnection>>,
    DeclineAgentConnectionMutationVariables
  > = (props) => {
    const { connectionId } = props ?? {};

    return declineAgentConnection(connectionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeclineAgentConnectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof declineAgentConnection>>
>;

export type DeclineAgentConnectionMutationError = ErrorType<ApiErrorDto>;
export type DeclineAgentConnectionMutationVariables = { connectionId: string };

/**
 * @summary Turn the agent away; it can sign in again later to ask again.
 */
export const useDeclineAgentConnection = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof declineAgentConnection>>,
      TError,
      DeclineAgentConnectionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof declineAgentConnection>>,
  TError,
  DeclineAgentConnectionMutationVariables,
  TContext
> => {
  return useMutation(getDeclineAgentConnectionMutationOptions(options), queryClient);
};
